import { Permission } from '@bookora/shared';

import { joinFa, quoted } from './text';
import type { TourDef } from './types';

/**
 * «داشبورد» (`/`). Every step is behind the key its card or button is drawn
 * with (`dashboard-page.tsx`, `dashboard/hooks.ts`): the quick actions each
 * behind their create key, today's appointments behind appointment.view, the
 * follow-ups behind session.view. The suggestions card appears only when the
 * guide has something for this person today, so its step often skips itself.
 */
export const DASHBOARD_TOUR: TourDef = {
  key: 'dashboard',
  version: 1,
  surface: 'page',
  title: 'داشبورد',
  hubDescription: 'کارهای امروز، نوبت‌ها، پیگیری‌ها و پیشنهادها در یک نگاه.',
  offer: {
    body: 'در چند قدم کوتاه نشونتون می‌دیم کارهای هر روز از همین صفحه چطور شروع می‌شه.',
  },
  route: { path: '/', match: (pathname) => pathname === '/' },
  autoOffer: true,
  maxSteps: 4,
  minSteps: 2,
  steps: [
    {
      id: 'actions',
      anchor: 'dashboard.actions',
      placement: 'bottom-end',
      when: (ctx) =>
        ctx.can(Permission.APPOINTMENT_CREATE) ||
        ctx.can(Permission.PATIENT_CREATE) ||
        ctx.can(Permission.SESSION_CREATE),
      title: 'کارهای روزانه',
      body: (ctx) => {
        const actions = [
          ctx.can(Permission.APPOINTMENT_CREATE) ? quoted('ثبت نوبت') : null,
          ctx.can(Permission.PATIENT_CREATE) ? quoted('بیمار جدید') : null,
          ctx.can(Permission.SESSION_CREATE) ? quoted('ثبت جلسه') : null,
        ].filter((x): x is string => x !== null);
        if (actions.length === 0) return 'کارهای روزانه از بالای همین صفحه شروع می‌شه.';
        return actions.length === 1
          ? `${joinFa(actions)} همیشه بالای همین صفحه است و فرمش رو مستقیم باز می‌کنه.`
          : `${joinFa(actions)} همیشه بالای همین صفحه است؛ هر کدام فرمش رو مستقیم باز می‌کنه.`;
      },
    },
    {
      id: 'today',
      anchor: 'dashboard.today',
      placement: 'auto',
      requires: { all: [Permission.APPOINTMENT_VIEW] },
      title: 'نوبت‌های امروز',
      body: (ctx) =>
        ctx.can(Permission.SESSION_CREATE)
          ? 'نوبت‌های امروز و فردا به ترتیب ساعت. وقتی بیمار رسید، دکمهٔ «ثبت جلسه درمان» کنار نوبتش می‌آد.'
          : 'نوبت‌های امروز و فردا به ترتیب ساعت، با وضعیت هر کدام.',
    },
    {
      id: 'followups',
      anchor: 'dashboard.followups',
      placement: 'auto',
      requires: { all: [Permission.SESSION_VIEW] },
      title: 'پیگیری‌ها',
      body: (ctx) =>
        ctx.can(Permission.SESSION_EDIT)
          ? 'تماس‌ها و یادآوری‌های امروز و هفتهٔ آینده، عقب‌افتاده‌ها بالاتر. بعد از هر تماس «انجام شد» رو بزنید، یا با «تعویق» به روز دیگه منتقل کنید.'
          : 'تماس‌ها و یادآوری‌های امروز و هفتهٔ آینده، عقب‌افتاده‌ها بالاتر.',
    },
    {
      id: 'suggestions',
      anchor: 'dashboard.suggestions',
      placement: 'auto',
      title: 'پیشنهادهای بوکورا',
      body: 'بر اساس اطلاعات کلینیک، کارهایی برای پیگیری پیشنهاد می‌دیم. اگر پیشنهادی به کارتون نمی‌آد، کنارش بذارید.',
    },
  ],
};
